import { useEffect, useState } from 'react';
import _, { isNil } from 'lodash';
import { Box } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import { COLUMN_TYPES, columnValueGetter } from '../../data-table/DataTableColumns';
import { formatMAC, SORT_ASC } from '../../../utilities/FormUtilities';
import { useArrayLoader } from '../../data-table/Loaders';
import DataTable from '../../data-table/DataTable';
import { AGENT_REPORTED_NETWORK_ADAPTERS, AGENTS_FUNCTION_NAME } from './AgentConstants';
import FormSection from '../../form/FormSection';

function getAdapterColumns() {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Interface`,
            sortable: false,
            mapping: 'name',
            width: 165,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`MAC`,
            sortable: false,
            mapping: 'mac',
            width: 165,
            formatter: formatMAC,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`IPv4 Address`,
            sortable: false,
            mapping: 'ipv4',
            width: 150,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`IPv4 Gateway`,
            sortable: false,
            mapping: 'gateway',
            width: 150,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`IPv6 Address`,
            hidden: false,
            sortable: false,
            mapping: 'ipv6',
            width: 320,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`IPv6 Gateway`,
            hidden: false,
            sortable: false,
            mapping: 'gatewayV6',
            width: 280,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Type`,
            sortable: false,
            mapping: 'type',
            formatter: (value) =>
                ({
                    wired: 'Wired',
                    wireless: 'Wireless',
                }[value] || 'Unknown'),
            width: 75,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Configuration`,
            sortable: false,
            mapping: 'interface_usage',
            formatter: (value) =>
                ({
                    test: 'Service Tests',
                    mgmt: 'Management',
                    both: 'Manual (All)',
                    auto: 'Default (All)',
                }[value] || ''),
            getTooltip: (row, column) => {
                const { tooltip } = column;

                if (tooltip === false || isNil(tooltip)) return null;
                if (tooltip === true) {
                    const value = columnValueGetter(row, column);

                    if (value === '') return value;
                    if (value === 'Service Tests') return 'Service Test Traffic';
                    if (value === 'Management') return 'nPoint Management Traffic';
                    if (value === 'Manual (All)')
                        return 'Service Test and nPoint Management Traffic (Manually Selected)';
                    if (value === 'Default (All)') return 'Service Test and nPoint Management Traffic (Auto Selected)';
                    return value;
                }
            },
            width: 150,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Description`,
            sortable: false,
            mapping: 'description',
            flex: 1,
        },
    ];
}

const initialSort = [{ property: 'name', direction: SORT_ASC }];

function hasInterfaces(agent, isHardware3kAgent) {
    const interfaceTypeKey = 'type';

    if (isHardware3kAgent) {
        return true;
    }

    if (!agent) {
        return false;
    }

    const firstInterface = _.first(_.get(agent, 'network.reportedInterfaces.interfaces', []));
    if (!firstInterface) {
        return false;
    }

    if (!_.has(firstInterface, interfaceTypeKey)) {
        return false;
    }

    return true;
}

export function AgentDetailAdapters({ latestAgentData, isHardware3kAgent }) {
    const [adapterList, setAdapterList] = useState([]);
    const adapterLoader = useArrayLoader(adapterList, (data) => data);
    const showInterfaces = hasInterfaces(latestAgentData, isHardware3kAgent.current);

    useEffect(() => {
        setAdapterList(_.get(latestAgentData, AGENT_REPORTED_NETWORK_ADAPTERS));
    }, [latestAgentData]);

    return (
        <>
            {showInterfaces && (
                <FormSection title={<Trans>Network Interfaces</Trans>}>
                    <Box mt={4}>
                        <DataTable
                            tableId={`${AGENTS_FUNCTION_NAME}-adapters`}
                            loader={adapterLoader}
                            columns={getAdapterColumns()}
                            visibleRows={5}
                            showToolbar={false}
                            showSearch={false}
                            sorters={initialSort}
                        />
                    </Box>
                </FormSection>
            )}
        </>
    );
}
